import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { HomeState} from '@/types/residents';
import { HomeAPI } from '@/services/residents/api/HomeAPI';

const initialState: HomeState = {
  events: [],
  bulletins: [],
  resources: [],
  linkedAccounts: [],
  unreadBulletinsCount: 0,
  selectedBulletinId: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchHomeData = createAsyncThunk(
  'home/fetchData',
  async (propertyId?: number) => {
    const [events, bulletins, resources, linkedAccounts, unreadBulletinsCount] = await Promise.all([
      HomeAPI.getEvents(),
      HomeAPI.getBulletins(propertyId),
      HomeAPI.getCommunityResources(propertyId),
      HomeAPI.getLinkedAccounts(),
      propertyId ? HomeAPI.getUnreadBulletinsCount(propertyId) : Promise.resolve(0),
    ]);
    
    // Filter out any undefined/null events and sort resources deterministically by position
    const validEvents = events.filter((event) => event && event.id);
    const sortedResources = (resources || []).slice().sort((a, b) => {
      const aPos = typeof a.position === 'number' ? a.position : 0;
      const bPos = typeof b.position === 'number' ? b.position : 0;
      return aPos - bPos;
    });
    
    if (events.length !== validEvents.length) {
      console.warn('🏠 fetchHomeData - Filtered out', events.length - validEvents.length, 'invalid events');
    }
    
    return { events: validEvents, bulletins, resources: sortedResources, linkedAccounts, unreadBulletinsCount };
  }
);

// Resources-only thunk: fetch only community resources and sort deterministically by position
export const fetchCommunityResources = createAsyncThunk(
  'home/fetchCommunityResources',
  async (propertyId?: number) => {
    const resources = await HomeAPI.getCommunityResources(propertyId);
    const sortedResources = (resources || []).slice().sort((a, b) => {
      const aPos = typeof a.position === 'number' ? a.position : 0;
      const bPos = typeof b.position === 'number' ? b.position : 0;
      return aPos - bPos;
    });
    return sortedResources;
  }
);

export const fetchEvents = createAsyncThunk(
  'home/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      const events = await HomeAPI.getEvents();
      // Let selectors handle sorting; just filter out invalid entries here
      const validEvents = events.filter((event) => event && event.id);
      if (events.length !== validEvents.length) {
        console.warn('🏠 Redux fetchEvents - Filtered out', events.length - validEvents.length, 'invalid events');
      }
      return validEvents;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch events'
      );
    }
  }
);

export const fetchBulletins = createAsyncThunk(
  'home/fetchBulletins',
  async (propertyId: number | undefined, { rejectWithValue }) => {
    try {
      const bulletins = await HomeAPI.getBulletins(propertyId);
      return bulletins;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch bulletins'
      );
    }
  }
);

export const fetchUnreadBulletinsCount = createAsyncThunk(
  'home/fetchUnreadBulletinsCount',
  async (propertyId: number) => {
    const count = await HomeAPI.getUnreadBulletinsCount(propertyId);
    return count;
  }
);

export const setEventRSVP = createAsyncThunk(
  'home/setEventRSVP',
  async ({ eventId, rsvp }: { eventId: number; rsvp: boolean }, { rejectWithValue }) => {
    try {
      await HomeAPI.setEventRSVP(eventId, rsvp);
      return { eventId, rsvp };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update RSVP'
      );
    }
  }
);

export const fetchLinkedAccounts = createAsyncThunk(
  'home/fetchLinkedAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const linkedAccounts = await HomeAPI.getLinkedAccounts();
      return linkedAccounts;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch linked accounts'
      );
    }
  }
);

export const deleteLinkedAccount = createAsyncThunk(
  'home/deleteLinkedAccount',
  async (accountId: number, { rejectWithValue }) => {
    try {
      await HomeAPI.deleteLinkedAccount(accountId);
      return accountId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete linked account'
      );
    }
  }
);

// Slice
const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLinkedAccounts: (state, action) => {
      state.linkedAccounts = action.payload;
    },
    removeLinkedAccount: (state, action) => {
      state.linkedAccounts = state.linkedAccounts.filter(
        account => account.id !== action.payload
      );
    },
    markBulletinAsRead: (state, action) => {
      const bulletin = state.bulletins.find(b => b.id === action.payload);
      if (bulletin) {
        // In iOS, bulletins are marked as read by setting messageReadCount to a non-zero value
        bulletin.messageReadCount = "1";
      }
    },
    setSelectedBulletinId: (state, action) => {
      state.selectedBulletinId = action.payload as number | null;
    },
    setUnreadBulletinsCount: (state, action) => {
      state.unreadBulletinsCount = typeof action.payload === 'number' ? action.payload : 0;
    },
    decrementUnreadBulletinsCount: (state, action) => {
      const by = typeof action.payload === 'number' ? action.payload : 1;
      state.unreadBulletinsCount = Math.max(0, (state.unreadBulletinsCount || 0) - by);
    },
  },
  extraReducers: (builder) => {
    // Fetch Home Data
    builder
      .addCase(fetchHomeData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = action.payload.events;
        state.bulletins = action.payload.bulletins;
        state.resources = action.payload.resources;
        state.linkedAccounts = action.payload.linkedAccounts;
        state.unreadBulletinsCount = action.payload.unreadBulletinsCount;
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to load home data';
      });
    
    // Fetch Events
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = action.payload;
        state.error = null;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch events';
      });
    
    // Fetch Bulletins
    builder
      .addCase(fetchBulletins.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBulletins.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bulletins = action.payload;
        state.error = null;
      })
      .addCase(fetchBulletins.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch bulletins';
      });
    
    // Fetch Unread Bulletins Count
    builder
      .addCase(fetchUnreadBulletinsCount.fulfilled, (state, action) => {
        state.unreadBulletinsCount = action.payload;
      });

    // Fetch Community Resources only
    builder
      .addCase(fetchCommunityResources.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCommunityResources.fulfilled, (state, action) => {
        state.isLoading = false;
        state.resources = action.payload;
        state.error = null;
      })
      .addCase(fetchCommunityResources.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch community resources';
      });
    
    // Set Event RSVP
    builder
      .addCase(setEventRSVP.fulfilled, (state, action) => {
        const { eventId, rsvp } = action.payload;
        const event = state.events.find(e => e.id === eventId);
        if (event) {
          const wasRsvp = !!event.rsvp;
          event.rsvp = rsvp;
          if (wasRsvp !== rsvp) {
            const current = typeof event.nRsvps === 'number' ? event.nRsvps : 0;
            event.nRsvps = rsvp ? current + 1 : Math.max(0, current - 1);
          }
        }
      })
      .addCase(setEventRSVP.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to update RSVP';
      });
    
    // Fetch Linked Accounts
    builder
      .addCase(fetchLinkedAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLinkedAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.linkedAccounts = action.payload;
        state.error = null;
      })
      .addCase(fetchLinkedAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to fetch linked accounts';
      });
    
    // Delete Linked Account
    builder
      .addCase(deleteLinkedAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLinkedAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove the deleted account from the list
        state.linkedAccounts = state.linkedAccounts.filter(
          account => account.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteLinkedAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to delete linked account';
      });
  },
});

export const { clearError, setLinkedAccounts, removeLinkedAccount, markBulletinAsRead, setSelectedBulletinId, setUnreadBulletinsCount, decrementUnreadBulletinsCount } = homeSlice.actions;
export default homeSlice.reducer; 